import {format} from 'date-fns';
import {Avatar, Attributes, DataStatus, Separator, Flag} from 'platform/components';
import {Box, HStack, Heading, Icon, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useRentalEventPricing} from '../hooks/useRentalEventPricing';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {getColorPathByScheme} from '../utils/getColorPathByScheme';
import {getRentalStateAppearance} from '../utils/getRentalStateAppearance';

/**
 * Measured height of the seven attribute rows in spacing units, so swapping the spinner in and
 * out does not resize the tooltip under the cursor.
 */
const BASIC_INFORMATION_MIN_HEIGHT = 58;

interface RentalEventTooltipProps extends RequiredTestIdProps {
  event: RentalResourceCalendarEvent | Nullish;
  blockReasonLabels: Map<string, string>;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function RentalEventTooltip(props: RentalEventTooltipProps) {
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const pricing = useRentalEventPricing(props.event);

  const stateAppearance = getRentalStateAppearance(props.event?.rentalOrder?.state);

  if (isNil(props.event)) {
    return null;
  }

  const stateColor = getColorPathByScheme(stateAppearance.colorScheme);

  return (
    <Box padding={4}>
      <Box
        backgroundColor="palettes.white.10.100"
        borderRadius="medium"
        padding={4}
        boxShadow="elevation_4"
        minWidth={200}
        width="100%"
        border="1px solid"
        borderColor="palettes.neutral.40.100"
        data-testid={suffixTestId('tooltip', props)}
      >
        {props.event.type === 'block' ? (
          <VStack spacing={4}>
            <Heading size={3}>{i18n.t('entity.rental.labels.vehicleBlock')}</Heading>
            <Attributes
              size="third"
              rows={[
                {
                  label: i18n.t('entity.vehicle.labels.title'),
                  value: props.event.block?.vehicleTitle || EMPTY_PLACEHOLDER,
                },
                {
                  label: i18n.t('entity.vehicle.labels.licensePlate'),
                  value: props.event.block?.licensePlate || EMPTY_PLACEHOLDER,
                },
                {
                  label: i18n.t('entity.rental.labels.from'),
                  value: formatDateTime('dateTimeShort', props.event.start),
                },
                {
                  label: i18n.t('entity.rental.labels.to'),
                  value: formatDateTime('dateTimeShort', props.event.end),
                },
                {
                  label: i18n.t('general.labels.reason'),
                  value:
                    props.blockReasonLabels.get(props.event.block?.reasonId ?? '') ||
                    EMPTY_PLACEHOLDER,
                },
              ]}
            />
          </VStack>
        ) : (
          <VStack spacing={4}>
            <HStack spacing={2} align="center">
              <Heading size={3}>
                {`${props.event.rentalOrder?.number || EMPTY_PLACEHOLDER} - ${
                  props.event.rentalOrder?.vehicleTitle || EMPTY_PLACEHOLDER
                }`}
              </Heading>
              <Flag
                size="small"
                label={stateAppearance.label}
                colorScheme={stateAppearance.colorScheme}
              />
            </HStack>

            <HStack spacing={3} justify="space-between">
              <Box flex={1}>
                <HStack spacing={4} align="center">
                  <Avatar icon="navigation/arrow_upward" variant="square" />
                  <VStack>
                    <HStack spacing={2} align="center">
                      <Heading size={4}>{format(props.event.start, 'd.M.yyyy H:mm')}</Heading>
                      <Flag
                        size="small"
                        label={match(props.event.rentalOrder?.state)
                          .with('closed', always(i18n.t('page.rentalOrder.labels.closed')))
                          .otherwise(always(stateAppearance.label))}
                        colorScheme={stateAppearance.colorScheme}
                      />
                    </HStack>
                    <Text size="xSmall" color="secondary">
                      {props.event.rentalOrder?.address || EMPTY_PLACEHOLDER}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
              <Box flex={1}>
                <HStack spacing={4} align="center">
                  <Avatar icon="navigation/arrow_downward" variant="square" />
                  <VStack>
                    <HStack spacing={2} align="center">
                      <Heading size={4}>{format(props.event.end, 'd.M.yyyy H:mm')}</Heading>
                      <Flag
                        size="small"
                        label={stateAppearance.label}
                        colorScheme={stateAppearance.colorScheme}
                      />
                    </HStack>
                    <Text size="xSmall" color="secondary">
                      {props.event.rentalOrder?.returnAddress || EMPTY_PLACEHOLDER}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </HStack>

            <Separator spacing={0} />

            <HStack spacing={3}>
              <Box flex={1}>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 7px;
                  `}
                >
                  <VStack spacing={4}>
                    <HStack spacing={1} align="center">
                      <Icon size={4} value="sidebar/vehicles" color={stateColor} />
                      <Heading size={5}>{i18n.t('entity.vehicle.labels.vehicle')}</Heading>
                    </HStack>

                    <Attributes
                      size="third"
                      rows={[
                        {
                          label: i18n.t('entity.vehicle.labels.title'),
                          value: props.event.rentalOrder?.vehicleTitle || EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('entity.vehicle.labels.licensePlate'),
                          value: props.event.rentalOrder?.licensePlate || EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('entity.vehicle.labels.vin'),
                          value: props.event.rentalOrder?.vin || EMPTY_PLACEHOLDER,
                        },
                      ]}
                    />
                  </VStack>

                  <HStack spacing={1} align="center">
                    <Icon size={4} value="sidebar/customers" color={stateColor} />
                    <Heading size={5}>{i18n.t('entity.customer.labels.customer')}</Heading>
                  </HStack>

                  <Attributes
                    size="third"
                    rows={[
                      {
                        label: i18n.t('general.labels.name'),
                        value: props.event.rentalOrder?.customerName || EMPTY_PLACEHOLDER,
                      },
                    ]}
                  />
                </div>
              </Box>
              <Box flex={1}>
                <VStack spacing={4}>
                  <HStack spacing={1} align="center">
                    <Icon size={4} value="action/info" color={stateColor} />
                    <Heading size={5}>{i18n.t('general.labels.basicInformation')}</Heading>
                  </HStack>

                  <DataStatus
                    isLoading={pricing.isLoading}
                    minHeight={BASIC_INFORMATION_MIN_HEIGHT}
                    spinnerSize="small"
                    data-testid={suffixTestId('basicInformation', props)}
                  >
                    <Attributes
                      size="third"
                      rows={[
                        {
                          label: i18n.t('page.rentalOrder.labels.dailyRate'),
                          value: pricing.dailyRate
                            ? formatCurrency(pricing.dailyRate.amount, pricing.dailyRate.currency)
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('entity.vehicle.costs.expectedPriceWithoutVAT'),
                          value: pricing.expectedPrice
                            ? formatCurrency(
                                pricing.expectedPrice.amount,
                                pricing.expectedPrice.currency
                              )
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('entity.vehicle.costs.expectedPriceWithVAT'),
                          value: pricing.expectedPriceWithVat
                            ? formatCurrency(
                                pricing.expectedPriceWithVat.amount,
                                pricing.expectedPriceWithVat.currency
                              )
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('page.rentalOrder.labels.salePriceWithoutVAT'),
                          value: props.event.rentalOrder?.salePrice
                            ? formatCurrency(
                                props.event.rentalOrder.salePrice.amount,
                                props.event.rentalOrder.salePrice.currency
                              )
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: i18n.t('page.rentalOrder.labels.salePriceWithVAT'),
                          value: props.event.rentalOrder?.salePriceWithVat
                            ? formatCurrency(
                                props.event.rentalOrder.salePriceWithVat.amount,
                                props.event.rentalOrder.salePriceWithVat.currency
                              )
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: `${i18n.t('page.rentalOrder.labels.rent')} ${i18n.t('page.rentalOrder.labels.actualMileage')}`,
                          value: props.event.rentalOrder?.rentMileage
                            ? `${props.event.rentalOrder.rentMileage} ${i18n.t('general.metric.km')}`
                            : EMPTY_PLACEHOLDER,
                        },
                        {
                          label: `${i18n.t('page.rentalOrder.labels.return')} ${i18n.t('page.rentalOrder.labels.actualMileage')}`,
                          value: props.event.rentalOrder?.returnMileage
                            ? `${props.event.rentalOrder.returnMileage} ${i18n.t('general.metric.km')}`
                            : EMPTY_PLACEHOLDER,
                        },
                      ]}
                    />
                  </DataStatus>
                </VStack>
              </Box>
            </HStack>
          </VStack>
        )}
      </Box>
    </Box>
  );
}
